import React, { useState } from 'react';
import { useAuth, authFetch } from '../../context/AuthContext';

const TOKEN_KEY = 'cobrand_token';

const Field = ({
  label, type = 'text', value, onChange, placeholder, disabled, hint,
}: {
  label: string; type?: string; value: string;
  onChange?: (v: string) => void; placeholder?: string;
  disabled?: boolean; hint?: string;
}) => (
  <div>
    <label className="block text-xs font-semibold text-stone-500 mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange?.(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800
                 focus:outline-none focus:ring-2 focus:ring-amber-400
                 disabled:bg-stone-50 disabled:text-stone-400"
    />
    {hint && <p className="text-xs text-stone-400 mt-1">{hint}</p>}
  </div>
);

const Alert = ({ type, msg }: { type: 'success' | 'error'; msg: string }) => (
  <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm
    ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700'
                         : 'bg-red-50 border border-red-200 text-red-600'}`}>
    <span>{type === 'success' ? '' : '오류'}</span> {msg}
  </div>
);

export default function ProfilePage() {
  const { user, token, login, logout } = useAuth();

  /* ── 프로필 폼 ── */
  const [name,  setName]  = useState(user?.name  ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [profileMsg, setProfileMsg] = useState<{ type: 'success'|'error'; text: string } | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  /* ── 비밀번호 폼 ── */
  const [curPw,  setCurPw]  = useState('');
  const [newPw,  setNewPw]  = useState('');
  const [confPw, setConfPw] = useState('');
  const [pwMsg, setPwMsg]   = useState<{ type: 'success'|'error'; text: string } | null>(null);
  const [pwLoading, setPwLoading] = useState(false);

  /* ── 비밀번호 강도 ── */
  const pwStrength = (pw: string): { label: string; color: string; width: string } => {
    if (!pw) return { label: '', color: 'bg-stone-200', width: '0%' };
    let score = 0;
    if (pw.length >= 6)  score++;
    if (pw.length >= 10) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { label: '약함',   color: 'bg-red-400',    width: '25%' };
    if (score <= 3) return { label: '보통',   color: 'bg-amber-400',  width: '60%' };
    return              { label: '강함',   color: 'bg-green-500',  width: '100%' };
  };
  const strength = pwStrength(newPw);

  /* ── 프로필 저장 ── */
  const handleProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMsg(null);

    try {
      const res  = await authFetch('/api/auth/profile', {
        method: 'PATCH',
        body:   JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setProfileMsg({ type: 'error', text: data.error ?? '저장 실패' });
        return;
      }
      // 새 토큰으로 갱신
      localStorage.setItem(TOKEN_KEY, data.token);
      // 페이지 새로고침으로 AuthContext 상태 갱신
      window.location.reload();
    } catch {
      setProfileMsg({ type: 'error', text: '서버 오류가 발생했습니다.' });
    } finally {
      setProfileLoading(false);
    }
  };

  /* ── 비밀번호 변경 ── */
  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw.length < 6) { setPwMsg({ type: 'error', text: '새 비밀번호는 6자 이상이어야 합니다.' }); return; }
    if (newPw !== confPw) { setPwMsg({ type: 'error', text: '새 비밀번호가 일치하지 않습니다.' });  return; }

    setPwLoading(true);
    setPwMsg(null);

    try {
      const res  = await authFetch('/api/auth/password', {
        method: 'PATCH',
        body:   JSON.stringify({ current_password: curPw, password: newPw, confirm: confPw }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPwMsg({ type: 'error', text: data.error ?? '변경 실패' });
        return;
      }
      setPwMsg({ type: 'success', text: '비밀번호가 변경되었습니다. 다시 로그인해주세요.' });
      setCurPw(''); setNewPw(''); setConfPw('');
      // 3초 후 자동 로그아웃
      setTimeout(() => { logout(); }, 3000);
    } catch {
      setPwMsg({ type: 'error', text: '서버 오류가 발생했습니다.' });
    } finally {
      setPwLoading(false);
    }
  };

  const roleLabel: Record<string, string> = {
    admin: '관리자', partner: '파트너', viewer: '조회',
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-stone-900">내 프로필</h1>
        <p className="text-stone-500 text-sm mt-1">계정 정보 및 비밀번호 관리</p>
      </div>

      {/* ── 계정 요약 카드 ── */}
      <div className="bg-stone-900 text-white rounded-2xl p-6 mb-6 flex items-center gap-5">
        <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center text-2xl font-bold text-black flex-shrink-0">
          {user?.name?.[0] ?? '?'}
        </div>
        <div>
          <p className="text-lg font-bold">{user?.name}</p>
          <p className="text-stone-400 text-sm">{user?.email}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-medium">
              {roleLabel[user?.role ?? ''] ?? user?.role}
            </span>
            <span className="text-xs text-stone-500">{user?.partner}</span>
          </div>
        </div>
      </div>

      {/* ── 프로필 수정 ── */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-5">
        <h2 className="font-bold text-stone-800 mb-5 flex items-center gap-2">
          <span className="text-lg"></span> 기본 정보 수정
        </h2>
        <form onSubmit={handleProfile} className="space-y-4">
          <Field label="이름" value={name} onChange={setName} placeholder="홍길동" />
          <Field label="이메일" type="email" value={email} onChange={setEmail} placeholder="user@brand.com" />
          <Field
            label="소속 파트너사"
            value={`${user?.partner ?? ''}`}
            disabled
            hint="파트너사 변경은 관리자에게 문의하세요."
          />
          <Field
            label="권한"
            value={roleLabel[user?.role ?? ''] ?? ''}
            disabled
          />

          {profileMsg && <Alert type={profileMsg.type} msg={profileMsg.text} />}

          <div className="pt-1">
            <button
              type="submit"
              disabled={profileLoading || (!name.trim() && !email.trim())}
              className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold px-6 py-2.5 rounded-lg text-sm transition"
            >
              {profileLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>

      {/* ── 비밀번호 변경 ── */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="font-bold text-stone-800 mb-5 flex items-center gap-2">
          <span className="text-lg">🔑</span> 비밀번호 변경
        </h2>
        <form onSubmit={handlePassword} className="space-y-4">
          <Field
            label="현재 비밀번호"
            type="password"
            value={curPw}
            onChange={setCurPw}
            placeholder="현재 비밀번호 입력"
          />

          <div className="border-t border-stone-100 pt-4 space-y-4">
            <Field
              label="새 비밀번호 (6자 이상)"
              type="password"
              value={newPw}
              onChange={setNewPw}
              placeholder="새 비밀번호"
            />

            {/* 비밀번호 강도 바 */}
            {newPw && (
              <div>
                <div className="flex justify-between text-xs text-stone-400 mb-1">
                  <span>비밀번호 강도</span>
                  <span className={
                    strength.label === '강함' ? 'text-green-600' :
                    strength.label === '보통' ? 'text-amber-500' : 'text-red-500'
                  }>{strength.label}</span>
                </div>
                <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                    style={{ width: strength.width }}
                  />
                </div>
              </div>
            )}

            <div>
              <Field
                label="새 비밀번호 확인"
                type="password"
                value={confPw}
                onChange={setConfPw}
                placeholder="새 비밀번호 재입력"
              />
              {confPw && newPw !== confPw && (
                <p className="text-xs text-red-500 mt-1">비밀번호가 일치하지 않습니다.</p>
              )}
              {confPw && newPw === confPw && newPw.length >= 6 && (
                <p className="text-xs text-green-600 mt-1">✓ 비밀번호가 일치합니다.</p>
              )}
            </div>
          </div>

          {pwMsg && <Alert type={pwMsg.type} msg={pwMsg.text} />}

          <div className="pt-1">
            <button
              type="submit"
              disabled={pwLoading || !curPw || !newPw || !confPw}
              className="bg-stone-800 hover:bg-stone-900 disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded-lg text-sm transition"
            >
              {pwLoading ? '변경 중...' : '비밀번호 변경'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
